import {
  QrCode, RefreshCw, HelpCircle, Smartphone,
  Send, ThumbsUp, ThumbsDown, ListMusic, MessageSquare,
  BarChart3, Star, GripVertical, Search,
} from 'lucide-react';
import { AnnotationOverlay, Annotation } from './Annotations';

function MockFrame({ children, annotations = [] }: { children: React.ReactNode; annotations?: Annotation[] }) {
  return (
    <div
      className="relative bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden p-3"
      style={{ maxWidth: 320 }}
    >
      {children}
      <AnnotationOverlay annotations={annotations} />
    </div>
  );
}

type TabId = 'request' | 'vote' | 'my-requests' | 'messages' | 'stats' | 'feedback' | 'rankings';

interface TabDef {
  id: TabId;
  label: string;
  icon: React.ElementType;
}

const standardTabs: TabDef[] = [
  { id: 'request', label: 'Request', icon: Send },
  { id: 'vote', label: 'Vote', icon: ThumbsUp },
  { id: 'my-requests', label: 'My Requests', icon: ListMusic },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'feedback', label: 'Feedback', icon: Star },
];

const rankedTabs: TabDef[] = [
  { id: 'request', label: 'Request', icon: Send },
  { id: 'rankings', label: 'My Rankings', icon: Star },
  { id: 'my-requests', label: 'My Requests', icon: ListMusic },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'feedback', label: 'Feedback', icon: Star },
];

export function MockHeader({ annotations = [] }: { annotations?: Annotation[] }) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center justify-between py-2">
        <button className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
          <QrCode className="w-4 h-4 text-gray-500" />
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate mx-2">Friday Night Dance</span>
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
          <button className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
            <HelpCircle className="w-4 h-4 text-gray-500" />
          </button>
          <button className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800">
            <Smartphone className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>
    </MockFrame>
  );
}

export function MockTabBar({
  highlightTabs = [],
  activeTab,
  isRankedMode = false,
  annotations = [],
}: {
  highlightTabs?: TabId[];
  activeTab?: TabId;
  isRankedMode?: boolean;
  annotations?: Annotation[];
}) {
  const tabs = isRankedMode ? rankedTabs : standardTabs;
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center justify-between gap-0.5 py-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tab.id === activeTab;
          const isHighlighted = highlightTabs.includes(tab.id);
          return (
            <div
              key={tab.id}
              className={`flex flex-col items-center gap-0.5 px-1 py-1 rounded-lg text-center flex-1 min-w-0 ${
                isActive ? 'bg-primary/10' : ''
              } ${isHighlighted ? 'ring-2 ring-primary rounded-lg' : ''}`}
            >
              <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-primary' : 'text-gray-400'}`} />
              <span className={`text-[9px] leading-tight truncate w-full ${isActive ? 'text-primary font-medium' : 'text-gray-400'}`}>
                {tab.label}
              </span>
            </div>
          );
        })}
      </div>
    </MockFrame>
  );
}

export function MockSearchBar({ annotations = [] }: { annotations?: Annotation[] }) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <Search className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-400">Search for a song or artist...</span>
      </div>
    </MockFrame>
  );
}

const placeholderSongs = [
  { title: 'Bohemian Rhapsody', artist: 'Queen', color: 'bg-red-300' },
  { title: 'Shake It Off', artist: 'Taylor Swift', color: 'bg-blue-300' },
  { title: 'Happy', artist: 'Pharrell Williams', color: 'bg-yellow-300' },
];

export function MockSearchResults({ annotations = [] }: { annotations?: Annotation[] }) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex flex-col gap-1">
        {placeholderSongs.map((song, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 p-2 rounded-lg ${i === 0 ? 'bg-primary/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
          >
            <div className={`w-8 h-8 rounded ${song.color} flex-shrink-0`} />
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
              <p className="text-[10px] text-gray-500 truncate">{song.artist}</p>
            </div>
          </div>
        ))}
      </div>
    </MockFrame>
  );
}

export function MockSubmitForm({ annotations = [] }: { annotations?: Annotation[] }) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded bg-red-300 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-900 dark:text-white">Bohemian Rhapsody</p>
            <p className="text-[10px] text-gray-500">Queen</p>
          </div>
        </div>
        <input
          type="text"
          disabled
          placeholder="Add a message or dedication..."
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 w-full"
        />
        <button className="btn btn-primary text-xs py-1.5 w-full" disabled>
          Submit Request
        </button>
      </div>
    </MockFrame>
  );
}

export function MockNickname({ annotations = [] }: { annotations?: Annotation[] }) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center justify-between py-1.5">
        <span className="text-xs text-gray-500">
          Requesting as <span className="font-semibold text-gray-900 dark:text-white">Guest-a1b2</span>
        </span>
        <button className="text-xs text-primary font-medium">Change</button>
      </div>
    </MockFrame>
  );
}

export function MockQueueCard({
  showDownvote = false,
  isUpvoted = false,
  isDownvoted = false,
  voteCount = 3,
  annotations = [],
}: {
  showDownvote?: boolean;
  isUpvoted?: boolean;
  isDownvoted?: boolean;
  voteCount?: number;
  annotations?: Annotation[];
}) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center gap-2 py-1">
        <div className="w-8 h-8 rounded bg-red-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">Bohemian Rhapsody</p>
          <p className="text-[10px] text-gray-500 truncate">Queen</p>
        </div>
        <div className="flex items-center gap-1">
          <button className={`p-1 rounded ${isUpvoted ? 'text-primary bg-primary/10' : 'text-gray-400'}`}>
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-4 text-center">{voteCount}</span>
          {showDownvote && (
            <button className={`p-1 rounded ${isDownvoted ? 'text-red-500 bg-red-50 dark:bg-red-900/20' : 'text-gray-400'}`}>
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </MockFrame>
  );
}

export function MockQueueList({ annotations = [] }: { annotations?: Annotation[] }) {
  const songs = [
    { title: 'Bohemian Rhapsody', artist: 'Queen', votes: 12, color: 'bg-red-300' },
    { title: 'Shake It Off', artist: 'Taylor Swift', votes: 8, color: 'bg-blue-300' },
    { title: 'Happy', artist: 'Pharrell Williams', votes: 5, color: 'bg-yellow-300' },
  ];
  return (
    <MockFrame annotations={annotations}>
      <div className="flex flex-col gap-1">
        {songs.map((song, i) => (
          <div key={i} className="flex items-center gap-2 py-1">
            <div className={`w-8 h-8 rounded ${song.color} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
              <p className="text-[10px] text-gray-500 truncate">{song.artist}</p>
            </div>
            <div className="flex items-center gap-1">
              <button className="p-1 rounded text-gray-400">
                <ThumbsUp className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-4 text-center">{song.votes}</span>
            </div>
          </div>
        ))}
      </div>
    </MockFrame>
  );
}

export function MockRankingsList({ annotations = [] }: { annotations?: Annotation[] }) {
  const ranked = [
    { title: 'Bohemian Rhapsody', artist: 'Queen', pos: 1 },
    { title: 'Shake It Off', artist: 'Taylor Swift', pos: 2 },
    { title: 'Happy', artist: 'Pharrell Williams', pos: 3 },
  ];
  return (
    <MockFrame annotations={annotations}>
      <div className="flex flex-col gap-1">
        {ranked.map((song) => (
          <div key={song.pos} className="flex items-center gap-2 py-1">
            <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0">
              {song.pos}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
              <p className="text-[10px] text-gray-500 truncate">{song.artist}</p>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 py-1 border-t border-dashed border-gray-200 dark:border-gray-700 mt-1 pt-2">
          <span className="w-3 flex-shrink-0" />
          <span className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
            4
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-900 dark:text-white truncate">Uptown Funk</p>
            <p className="text-[10px] text-gray-500 truncate">Bruno Mars</p>
          </div>
          <button className="text-[10px] px-2 py-0.5 rounded-full bg-primary text-white font-medium">+Rank</button>
        </div>
      </div>
    </MockFrame>
  );
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  queued: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  nowPlaying: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  played: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  declined: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  overflow: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  queued: 'Queued',
  nowPlaying: 'Now Playing',
  played: 'Played',
  declined: 'Declined',
  overflow: 'In Overflow',
};

export function MockStatusBadge({
  status,
  annotations = [],
}: {
  status: 'pending' | 'queued' | 'nowPlaying' | 'played' | 'declined' | 'overflow';
  annotations?: Annotation[];
}) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center gap-2 py-1">
        <div className="w-8 h-8 rounded bg-red-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">Bohemian Rhapsody</p>
          <p className="text-[10px] text-gray-500 truncate">Queen</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[status]}`}>
          {statusLabels[status]}
        </span>
      </div>
    </MockFrame>
  );
}

export function MockMyRequests({ annotations = [] }: { annotations?: Annotation[] }) {
  const statuses: Array<{ status: string; title: string; color: string }> = [
    { status: 'pending', title: 'Bohemian Rhapsody', color: 'bg-red-300' },
    { status: 'queued', title: 'Shake It Off', color: 'bg-blue-300' },
    { status: 'nowPlaying', title: 'Happy', color: 'bg-yellow-300' },
  ];
  return (
    <MockFrame annotations={annotations}>
      <div className="flex flex-col gap-1">
        {statuses.map((s, i) => (
          <div key={i} className="flex items-center gap-2 py-1">
            <div className={`w-8 h-8 rounded ${s.color} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{s.title}</p>
              <p className="text-[10px] text-gray-500 truncate">Artist</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[s.status]}`}>
              {statusLabels[s.status]}
            </span>
          </div>
        ))}
      </div>
    </MockFrame>
  );
}

export function MockOverflowPool({ annotations = [] }: { annotations?: Annotation[] }) {
  const songs = [
    { title: 'Under Pressure', priority: 1 },
    { title: 'Stairway to Heaven', priority: 2 },
  ];
  return (
    <MockFrame annotations={annotations}>
      <div className="flex flex-col gap-1">
        {songs.map((song, i) => (
          <div key={i} className="flex items-center gap-2 py-1 pl-2 border-l-2 border-indigo-400">
            <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="w-4 h-4 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
              {song.priority}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
              <p className="text-[10px] text-gray-500 truncate">Artist</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors.overflow}`}>
              In Overflow
            </span>
          </div>
        ))}
      </div>
    </MockFrame>
  );
}

export function MockOverflowPromoted({ annotations = [] }: { annotations?: Annotation[] }) {
  return (
    <MockFrame annotations={annotations}>
      <div className="flex items-center gap-2 py-1">
        <div className="w-8 h-8 rounded bg-purple-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">Under Pressure</p>
          <p className="text-[10px] text-gray-500 truncate">Queen & David Bowie</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors.queued}`}>
          In Queue
        </span>
      </div>
    </MockFrame>
  );
}
